import { supabase } from './supabase';
import { PostgrestError } from '@supabase/supabase-js';

export interface Message {
  id: string;
  sender_id: string;
  receiver_id: string;
  content: string;
  image_url?: string;
  read: boolean;
  created_at: string;
  listing_id?: string;
}

export interface Chat {
  user_id: string;
  user_name: string;
  last_message?: Message;
  unread_count: number;
}

/**
 * Send a message to another user
 */
export async function sendMessage(
  receiverId: string, 
  content: string,
  image?: File | null,
  listingId?: string
): Promise<{ message: Message | null; error: PostgrestError | null }> {
  try {
    if (!receiverId) {
      console.error('No receiver ID provided');
      return { 
        message: null, 
        error: { message: 'No receiver ID provided', details: '', hint: '', code: 'INVALID_INPUT', name: 'InputError' } as PostgrestError 
      };
    }

    const { data, error: userError } = await supabase.auth.getSession();
    
    if (userError || !data.session?.user) {
      console.error('Authentication error:', userError);
      return { 
        message: null, 
        error: { message: 'Not authenticated', details: '', hint: '', code: 'UNAUTHORIZED', name: 'AuthError' } as PostgrestError 
      };
    }
    
    const user = data.session.user;
    
    // Validate user object
    if (!user || !user.id) {
      console.error('Invalid user object in sendMessage');
      return { 
        message: null, 
        error: { message: 'Invalid user session', details: '', hint: '', code: 'INVALID_USER', name: 'AuthError' } as PostgrestError 
      };
    }

    // Safer development logging
    if (process.env.NODE_ENV !== 'production') {
      console.log('Sending message:', {
        sender_id: user.id,
        receiver_id: receiverId,
        content,
        listing_id: listingId
      });
    }

    let imageUrl = null;
    
    // Handle image upload if provided
    if (image && image instanceof File) {
      // Only log in development
      if (import.meta.env.DEV) console.log('Uploading message image');
      const fileExt = image.name.split('.').pop();
      const fileName = `${user.id}/${Date.now()}.${fileExt}`;
      const filePath = `message-images/${fileName}`;
      
      const { error: uploadError } = await supabase.storage
        .from('message-images')
        .upload(filePath, image);
        
      if (uploadError) throw uploadError;
      
      const { data: urlData } = supabase.storage
        .from('message-images')
        .getPublicUrl(filePath);
        
      imageUrl = urlData.publicUrl;
    }

    // Create the message record
    const { data: messageData, error } = await supabase
      .from('messages')
      .insert({
        sender_id: user.id,
        receiver_id: receiverId,
        content,
        image_url: imageUrl,
        read: false,
        listing_id: listingId
      })
      .select('*')
      .single();

    if (error) {
      console.error('Error creating message:', error);
      throw error;
    }

    console.log('Message created successfully:', messageData);
    
    // Invalidate all related caches since a new message changes the state
    chatCache = null;
    unreadCountCache = null;
    if (chatMessagesCache[receiverId]) {
      delete chatMessagesCache[receiverId];
    }

    // Fire-and-forget: ask the server to email the recipient if they're not
    // around to see it (the function re-validates everything and rate-limits).
    // The session token is attached explicitly — functions.invoke's internal
    // auth goes stale under this project's implicit-flow (see rewards.ts).
    supabase.auth
      .getSession()
      .then(({ data: { session } }) =>
        supabase.functions.invoke('notify-new-message', {
          body: { message_id: messageData.id },
          headers: session?.access_token
            ? { Authorization: `Bearer ${session.access_token}` }
            : undefined,
        }),
      )
      .catch(() => {});

    return { message: messageData, error: null };
  } catch (error) {
    console.error('Error sending message:', error);
    return { message: null, error: error as PostgrestError };
  }
}

/**
 * Get all chats for the current user
 */
// Cache for storing chat data with a short TTL
interface ChatCache {
  data: { chats: Chat[]; error: PostgrestError | null };
  timestamp: number;
}

let chatCache: ChatCache | null = null;
const CACHE_TTL = 30000; // 30 seconds cache TTL

export async function getChats(): Promise<{ chats: Chat[]; error: PostgrestError | null }> {
  // Check if we have valid cached data
  if (chatCache && (Date.now() - chatCache.timestamp < CACHE_TTL)) {
    // Safer development logging that works in production builds
    if (process.env.NODE_ENV !== 'production') {
      console.log('Using cached chat data');
    }
    return chatCache.data;
  }
  try {
    const { data: userData, error: userError } = await supabase.auth.getUser();
    
    if (userError) {
      console.error('Auth error in getChats:', userError);
      return { chats: [], error: { 
        message: userError.message, 
        details: '', 
        hint: '', 
        code: 'AUTH_ERROR',
        name: 'AuthError'
      } as PostgrestError };
    }
    
    if (!userData?.user) {
      console.error('No authenticated user in getChats');
      return { chats: [], error: {
        message: 'Not authenticated', 
        details: '', 
        hint: '', 
        code: 'AUTH_ERROR',
        name: 'AuthError'
      } as PostgrestError };
    }
    
    const user = userData.user;
    
    // Additional validation to ensure we have a valid user object
    if (!user || !user.id) {
      console.error('Invalid user object in getChats');
      return { 
        chats: [], 
        error: { 
          message: 'Invalid user object', 
          details: '', 
          hint: '', 
          code: 'AUTH_ERROR',
          name: 'AuthError'
        } as PostgrestError 
      };
    }
    
    // Safer development logging
    if (process.env.NODE_ENV !== 'production') console.log('Getting chats for user:', user.id);

    // Get all messages for the current user
    const { data: messagesData, error } = await supabase
      .from('messages')
      .select('*')
      .or(`sender_id.eq.${user.id},receiver_id.eq.${user.id}`)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching messages:', error);
      return { chats: [], error };
    }

    // Safer development logging
    if (process.env.NODE_ENV !== 'production') console.log(`Retrieved ${messagesData?.length || 0} messages for user ${user.id}`);
    
    // If no messages, return empty array
    if (!messagesData || messagesData.length === 0) {
      return { chats: [], error: null };
    }
    
    // Get user information separately
    const uniqueUserIds = new Set<string>();
    messagesData.forEach((message: any) => {
      // Safely check sender and receiver IDs
      if (message.sender_id && message.sender_id !== user.id) uniqueUserIds.add(message.sender_id);
      if (message.receiver_id && message.receiver_id !== user.id) uniqueUserIds.add(message.receiver_id);
    });
    
    // Safer development logging
    if (process.env.NODE_ENV !== 'production') console.log('Unique user IDs in conversations:', Array.from(uniqueUserIds));
    
    // Display names come from profiles (never from email addresses — the
    // local-part of an email is PII and must not be shown to counterparties).
    const userProfiles: Record<string, string> = {};

    const idsToResolve = Array.from(uniqueUserIds).filter(Boolean);
    if (idsToResolve.length > 0) {
      try {
        // listing_owners is the PII-safe public view (display_name for every
        // user, no email) and is readable by any authenticated user.
        const { data: ownerRows, error: ownersError } = await supabase
          .from('listing_owners')
          .select('id, display_name')
          .in('id', idsToResolve);
        if (ownersError) {
          console.error('Error fetching chat display names:', ownersError);
        } else {
          (ownerRows || []).forEach((row: any) => {
            if (row.id && row.display_name) userProfiles[row.id] = row.display_name;
          });
        }
      } catch (error) {
        console.error('Error fetching chat display names:', error);
      }
    }
    for (const userId of idsToResolve) {
      if (!userProfiles[userId]) {
        userProfiles[userId] = `Member ${userId.substring(0, 4)}`;
      }
    }

    // Group messages by chat and format the response
    const chatsMap = new Map<string, Chat>();
    
    messagesData.forEach((message: any) => {
      const otherUserId = message.sender_id === user.id 
        ? message.receiver_id 
        : message.sender_id;
      
      const otherUserName = userProfiles[otherUserId] || `Member ${otherUserId.substring(0, 4)}`;

      if (!chatsMap.has(otherUserId)) {
        chatsMap.set(otherUserId, {
          user_id: otherUserId,
          user_name: otherUserName,
          last_message: message,
          unread_count: !message.read && message.sender_id !== user.id ? 1 : 0
        });
      } else {
        const chat = chatsMap.get(otherUserId)!;
        const existingTimestamp = new Date(chat.last_message?.created_at || 0).getTime();
        const newTimestamp = new Date(message.created_at).getTime();
        
        // Update last message if this one is newer
        if (newTimestamp > existingTimestamp) {
          chat.last_message = message;
        }
        
        if (!message.read && message.sender_id !== user.id) {
          chat.unread_count++;
        }
      }
    });

    const chatsList = Array.from(chatsMap.values());
    // Safer development logging
    if (process.env.NODE_ENV !== 'production') console.log(`Returning ${chatsList.length} chats for user ${user.id}`);
    
    // Store in cache
    const result = { chats: chatsList, error: null };
    chatCache = {
      data: result,
      timestamp: Date.now()
    };
    
    return result;
  } catch (error) {
    console.error('Error getting chats:', error);
    return { chats: [], error: error as PostgrestError };
  }
}

/**
 * Cache for storing chat messages with a short TTL
 */
interface ChatMessagesCache {
  [key: string]: {
    data: { messages: Message[]; error: PostgrestError | null };
    timestamp: number;
  };
}

let chatMessagesCache: ChatMessagesCache = {};
const MESSAGES_CACHE_TTL = 30000; // 30 seconds cache TTL

/**
 * Get messages between the current user and another user
 */
export async function getChatMessages(otherUserId: string): Promise<{ messages: Message[]; error: PostgrestError | null }> {
  // Check if we have valid cached data for this conversation
  if (chatMessagesCache[otherUserId] && 
      (Date.now() - chatMessagesCache[otherUserId].timestamp < MESSAGES_CACHE_TTL)) {
    console.log('Using cached message data for', otherUserId);
    return chatMessagesCache[otherUserId].data;
  }
  try {
    const { data: userData, error: userError } = await supabase.auth.getUser();
    
    if (userError) {
      return { messages: [], error: { 
        message: userError.message, 
        details: '', 
        hint: '', 
        code: 'AUTH_ERROR',
        name: 'AuthError'
      } as PostgrestError };
    }
    
    if (!userData?.user) {
      return { messages: [], error: {
        message: 'Not authenticated', 
        details: '', 
        hint: '', 
        code: 'AUTH_ERROR',
        name: 'AuthError'
      } as PostgrestError };
    }
    
    const user = userData.user;

    const { data, error } = await supabase
      .from('messages')
      .select('*')
      .or(`and(sender_id.eq.${user.id},receiver_id.eq.${otherUserId}),and(sender_id.eq.${otherUserId},receiver_id.eq.${user.id})`)
      .order('created_at', { ascending: true });

    // Cache results
    const result = { messages: data || [], error };
    chatMessagesCache[otherUserId] = {
      data: result,
      timestamp: Date.now()
    };
    
    return result;
  } catch (error) {
    console.error('Error getting chat messages:', error);
    return { messages: [], error: error as PostgrestError };
  }
}

/**
 * Mark messages from a specific sender as read
 */
export async function markMessagesAsRead(senderId: string): Promise<{ error: PostgrestError | null }> {
  // Invalidate cache when marking messages as read
  if (chatMessagesCache[senderId]) {
    delete chatMessagesCache[senderId];
  }
  
  // Also invalidate related caches as unread counts will change
  chatCache = null;
  unreadCountCache = null;
  try {
    const { data: userData, error: userError } = await supabase.auth.getUser();
    
    if (userError) {
      return { error: { 
        message: userError.message, 
        details: '', 
        hint: '', 
        code: 'AUTH_ERROR',
        name: 'AuthError'
      } as PostgrestError };
    }
    
    if (!userData?.user) {
      return { error: {
        message: 'Not authenticated', 
        details: '', 
        hint: '', 
        code: 'AUTH_ERROR',
        name: 'AuthError'
      } as PostgrestError };
    }
    
    const user = userData.user;

    const { error } = await supabase
      .from('messages')
      .update({ read: true })
      .eq('sender_id', senderId)
      .eq('receiver_id', user.id)
      .eq('read', false);

    return { error };
  } catch (error) {
    console.error('Error marking messages as read:', error);
    return { error: error as PostgrestError };
  }
}

/**
 * Subscribe to new messages for the current user
 */
export function subscribeToMessages(callback: (message: Message) => void) {
  // Get current session asynchronously
  const sessionPromise = supabase.auth.getSession().then(({ data, error }) => {
    if (error) {
      console.error('Auth error when setting up subscription:', error);
      return null;
    }
    
    if (!data.session?.user) {
      console.error('No authenticated user for subscription');
      return null;
    }
    
    const user = data.session.user;

    console.log('Setting up message subscription for user:', user.id);

    // Create a unique channel name for this user to avoid conflicts
    const channelName = `messages-${user.id}-${Date.now()}`;
    
    const subscription = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `receiver_id=eq.${user.id}`
        },
        (payload) => {
          console.log('Received new message for user', user.id, ':', payload.new);
          callback(payload.new as Message);
        }
      )
      .subscribe((status) => {
        console.log(`Subscription status for ${user.id}:`, status);
      });

    // Also load existing messages immediately to ensure we don't miss any
    setTimeout(() => {
      refreshUnreadMessages(user.id).then(messages => {
        console.log('Loaded unread messages on subscription:', messages.length);
        messages.forEach(message => callback(message));
      });
    }, 1000);
      
    return subscription;
  });
  
  // Return an object with an unsubscribe method that waits for the session promise
  return {
    unsubscribe: async () => {
      const subscription = await sessionPromise;
      if (subscription) {
        subscription.unsubscribe();
      }
    }
  };
}

/**
 * Helper function to refresh unread messages
 */
async function refreshUnreadMessages(userId: string): Promise<Message[]> {
  try {
    const { data, error } = await supabase
      .from('messages')
      .select('*')
      .eq('receiver_id', userId)
      .eq('read', false)
      .order('created_at', { ascending: true });
      
    if (error) {
      console.error('Error fetching unread messages:', error);
      return [];
    }
    
    return data || [];
  } catch (e) {
    console.error('Exception fetching unread messages:', e);
    return [];
  }
}

/**
 * Cache for storing unread count with a short TTL
 */
interface UnreadCountCache {
  data: { count: number; error: PostgrestError | null };
  timestamp: number;
}

let unreadCountCache: UnreadCountCache | null = null;
const UNREAD_COUNT_CACHE_TTL = 30000; // 30 seconds cache TTL

/**
 * Get the total count of unread messages for the current user
 */
export async function getUnreadCount(): Promise<{ count: number; error: PostgrestError | null }> {
  // Check if we have valid cached data
  if (unreadCountCache && (Date.now() - unreadCountCache.timestamp < UNREAD_COUNT_CACHE_TTL)) {
    console.log('Using cached unread count data');
    return unreadCountCache.data;
  }
  try {
    const { data: userData, error: userError } = await supabase.auth.getUser();
    
    if (userError) {
      return { count: 0, error: { 
        message: userError.message, 
        details: '', 
        hint: '', 
        code: 'AUTH_ERROR',
        name: 'AuthError'
      } as PostgrestError };
    }
    
    if (!userData?.user) {
      return { count: 0, error: {
        message: 'Not authenticated', 
        details: '', 
        hint: '', 
        code: 'AUTH_ERROR',
        name: 'AuthError'
      } as PostgrestError };
    }
    
    const user = userData.user;

    const { count, error } = await supabase
      .from('messages')
      .select('id', { count: 'exact', head: true })
      .eq('receiver_id', user.id)
      .eq('read', false);
    
    if (error) return { count: 0, error };
    
    // Cache the result
    const result = { count: count || 0, error: null };
    unreadCountCache = {
      data: result,
      timestamp: Date.now()
    };
    
    return result;
  } catch (error) {
    console.error('Error getting unread count:', error);
    return { count: 0, error: error as PostgrestError };
  }
}